// src/components/Seo.tsx
import { Helmet } from 'react-helmet-async';

type Json = Record<string, unknown>;
type Props = {
  readonly title: string;
  readonly description?: string;
  readonly canonicalPath?: string;
  readonly image?: string;
  readonly noindex?: boolean;
  readonly jsonLd?: Json | Json[];
};

let SITE_URL = import.meta.env.VITE_SITE_URL?.replace(/\/$/, '');
if (!SITE_URL) {
  if (globalThis?.window !== undefined && globalThis?.location?.origin) {
    SITE_URL = globalThis.location.origin;
  } else {
    SITE_URL = 'https://example.com';
  }
}

const toAbs = (url?: string) => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/') ? url : '/' + url;
  return SITE_URL + path;
};

export default function Seo({
  title,
  description,
  canonicalPath,
  image,
  noindex,
  jsonLd,
}: Props) {
  let canonical: string | undefined = toAbs(canonicalPath);
  if (!canonical && globalThis.window !== undefined) {
    canonical = globalThis.location.href;
  }
  const ogImage = toAbs(image);

  let blocks: Json[] = [];
  if (Array.isArray(jsonLd)) {
    blocks = jsonLd;
  } else if (jsonLd) {
    blocks = [jsonLd];
  }

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        {noindex && <meta name="robots" content="noindex,nofollow" />}

        {/* Canonical */}
        {canonical && <link rel="canonical" href={canonical} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        {description && <meta property="og:description" content={description} />}
        {canonical && <meta property="og:url" content={canonical} />}
        {ogImage && <meta property="og:image" content={ogImage} />}

        {/* Twitter */}
        <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={title} />
        {description && <meta name="twitter:description" content={description} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>

      {/* Additional JSON-LD blocks */}
      {blocks.map((b) => (
        <script key={JSON.stringify(b)} type="application/ld+json" suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', ...b }) }}
        />
      ))}
    </>
  );
}
